import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  useTheme,
  Button,
  TextField,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Stack,
  Tooltip,
  LinearProgress,
  alpha,
  Card,
  CardContent,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  LocalShipping as LocalShippingIcon,
  Search as SearchIcon,
  Inbox as InboxIcon,
} from "@mui/icons-material";
import PageHeader from "../../components/PageHeader";
import LoadingSkeleton from "../../components/LoadingSkeleton";
import { SetupCoachmark } from '../../components/selfSetup/SetupCoachmark';
import { useSelfSetupTour } from '../../hooks/useSelfSetupTour';
import { useConfirm } from '../../hooks/useConfirm';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { elevatedPanelSx } from "../../constants/shape";
import StatusChip from "../../components/StatusChip";
import { useTruckStore } from "../../stores/transportation.store";
import { useOrganizationStore } from "../../stores/organization.store";
import {
  TruckSchema,
  TruckStatus,
  CapacityUnit,
  EngineType,
} from "../../types/transportation.type";
import { isValidSaudiPlate } from "../../components/PlateInput";
import VehicleFormDialog, {
  type VehicleFormState,
} from "../../components/transportation/VehicleFormDialog";

function normalizeTruckFeatures(next: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of next) {
    const s = String(raw).trim();
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

function FleetPlateBadge({
  enPlate,
  arPlate,
}: {
  enPlate?: string | null;
  arPlate?: string | null;
}) {
  const theme = useTheme();
  const hasPlate = Boolean(enPlate?.trim() || arPlate?.trim());

  if (!hasPlate) {
    return (
      <Typography variant="body2" color="text.secondary">
        â€”
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        display: "inline-flex",
        alignItems: "stretch",
        bgcolor: theme.palette.background.paper,
        overflow: "hidden",
        height: 34,
        minWidth: 96,
      }}
    >
      <Box
        sx={{
          px: 1,
          py: 0.5,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: "1px",
          flex: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            lineHeight: 1.1,
            fontSize: '0.75rem',
            color: "text.primary",
            letterSpacing: "0.04em",
          }}
        >
          {enPlate?.trim() || "â€”"}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            lineHeight: 1.1,
            fontSize: '0.75rem',
            color: "text.secondary",
          }}
        >
          {arPlate?.trim() || "â€”"}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 22,
          bgcolor: "common.black",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderInlineStart: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          sx={{
            color: "common.white",
            fontSize: '0.6rem',
            fontWeight: 700,
            transform: "rotate(90deg)",
            letterSpacing: "0.05em",
          }}
        >
          KSA
        </Typography>
      </Box>
    </Box>
  );
}

const FleetManagement: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirm();
  const { canMutate } = useAuthPermissions();
  const [open, setOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<TruckSchema | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");
  /** Knowledge Base "Go to Add vehicle" guided tips. */
  type HelpAddPhase = 'addButton' | 'form' | 'submit';
  const [helpAddPhase, setHelpAddPhase] = useState<HelpAddPhase | null>(null);
  /** Knowledge Base "Go to Edit or delete vehicle" row-action tips. */
  type HelpRowActionsPhase = 'edit' | 'delete';
  const [helpRowActionsPhase, setHelpRowActionsPhase] =
    useState<HelpRowActionsPhase | null>(null);
  const {
    isStep,
    tourPhase,
    completeStep,
    dismissTour,
    setTourPhase,
    advanceTourPhase,
  } = useSelfSetupTour();
  const tourFiltersHint = isStep('vehicles') && tourPhase === 'filters';
  const tourTableHint = isStep('vehicles') && tourPhase === 'table';
  const tourAddHint = isStep('vehicles') && tourPhase === 'addButton';
  const tourFormHint =
    (isStep('vehicles') && tourPhase === 'form') || helpAddPhase === 'form';
  const tourSubmitHint =
    (isStep('vehicles') && tourPhase === 'submit') || helpAddPhase === 'submit';
  const tourDialogOpen = tourFormHint || tourSubmitHint;
  const [dismissAddHint, setDismissAddHint] = useState(false);

  // --- SNACKBAR STATE ---
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [formData, setFormData] = useState<VehicleFormState>({
    name: "",
    capacity: 0,
    status: TruckStatus.AVAILABLE as TruckStatus,
    data: {
      en_plate_number: "",
      ar_plate_number: "",
      car_model: "",
      vehicle_type: "",
      capacity_unit: undefined as CapacityUnit | null | undefined,
      empty_weight: undefined as number | undefined,
      fuel_consumption_km_per_l: undefined as number | undefined,
      engine_type: undefined as EngineType | null | undefined,
      vin: "",
      price_per_trip: undefined as number | undefined,
      truck_features: [] as string[],
    },
  });

  const {
    trucks,
    trucksLoading,
    fetchTrucks,
    createTruck,
    updateTruck,
    deleteTruck,
  } = useTruckStore();
  const { selectedOrganization } = useOrganizationStore();

  const orgId = selectedOrganization?.id;
  const canCreateTruck = Boolean(orgId && canMutate(`recyclee.${orgId}.*.truck.create`));
  const canEditTruck = Boolean(orgId && canMutate(`recyclee.${orgId}.*.truck.write`));
  const canDeleteTruck = Boolean(orgId && canMutate(`recyclee.${orgId}.*.truck.delete`));

  React.useEffect(() => {
    if (selectedOrganization) {
      fetchTrucks({ organization_id: selectedOrganization.id });
    }
  }, [fetchTrucks, selectedOrganization]);

  // --- SNACKBAR HANDLERS ---
  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "warning" = "success"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const handleOpen = (vehicle?: TruckSchema) => {
    if (vehicle) {
      setSelectedVehicle(vehicle);
      setFormData({
        name: vehicle.name || "",
        capacity: vehicle.capacity || 0,
        status: vehicle.status,
        data: {
          en_plate_number: vehicle.data?.en_plate_number || "",
          ar_plate_number: vehicle.data?.ar_plate_number || "",
          car_model: vehicle.data?.car_model || "",
          vehicle_type: vehicle.data?.vehicle_type || "",
          capacity_unit: vehicle.data?.capacity_unit ?? undefined,
          empty_weight:
            typeof vehicle.data?.empty_weight === "number"
              ? vehicle.data.empty_weight
              : undefined,
          fuel_consumption_km_per_l:
            typeof vehicle.data?.fuel_consumption_km_per_l === "number"
              ? vehicle.data.fuel_consumption_km_per_l
              : undefined,
          engine_type: vehicle.data?.engine_type ?? undefined,
          vin: vehicle.data?.vin || "",
          price_per_trip:
            typeof vehicle.data?.price_per_trip === "number"
              ? vehicle.data.price_per_trip
              : undefined,
          truck_features: Array.isArray(vehicle.data?.truck_features)
            ? normalizeTruckFeatures(vehicle.data.truck_features as string[])
            : [],
        },
      });
    } else {
      setSelectedVehicle(null);
      setFormData({
        name: "",
        capacity: 0,
        status: TruckStatus.AVAILABLE,
        data: {
          en_plate_number: "",
          ar_plate_number: "",
          car_model: "",
          vehicle_type: "",
          capacity_unit: undefined,
          empty_weight: undefined,
          fuel_consumption_km_per_l: undefined,
          engine_type: undefined,
          vin: "",
          price_per_trip: undefined,
          truck_features: [],
        },
      });
    }
    setFormErrors({});
    setOpen(true);
  };

  useEffect(() => {
    const state = location.state as {
      openAdd?: boolean;
      showAddHint?: boolean;
      showRowActionsHint?: boolean;
    } | null;
    if (!state?.openAdd && !state?.showAddHint && !state?.showRowActionsHint) return;
    if (state.openAdd) handleOpen();
    if (state.showAddHint) {
      setHelpAddPhase('addButton');
      setDismissAddHint(false);
    }
    if (state.showRowActionsHint) {
      setSearch('');
      setHelpRowActionsPhase('edit');
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const formatCapacity = (vehicle: TruckSchema) => {
    const unit = vehicle.data?.capacity_unit;
    if (unit) {
      return `${vehicle.capacity.toLocaleString()} ${unit}`;
    }
    return vehicle.capacity.toLocaleString();
  };

  const finishHelpAddGuide = () => {
    setHelpAddPhase(null);
    setOpen(false);
    setSelectedVehicle(null);
    setFormErrors({});
  };

  const handleClose = () => {
    if (helpAddPhase) {
      finishHelpAddGuide();
      return;
    }
    if (
      (isStep('vehicles') && tourPhase === 'form') ||
      (isStep('vehicles') && tourPhase === 'submit')
    ) {
      dismissTour();
    }
    setOpen(false);
    setSelectedVehicle(null);
    setFormErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parts = formData.data.en_plate_number.split(" ");
    const letters = parts[0] || "";
    const numbers = parts[1] || "";

    if (!isValidSaudiPlate(letters, numbers)) {
      showSnackbar(
        t('transportation.fleet.invalidPlate'),
        "error"
      );
      return;
    }

    const err: Record<string, string> = {};
    if (!formData.name.trim()) {
      err.name = t('transportation.fleet.validation.nameRequired');
    }
    if (!(formData.capacity > 0)) {
      err.capacity = t('transportation.fleet.validation.capacityRequired');
    }
    setFormErrors(err);
    if (Object.keys(err).length > 0) return;

    if (selectedVehicle) {
      const result = await updateTruck(selectedVehicle.id, formData);
      if (result) {
        showSnackbar(t('transportation.fleet.vehicleUpdated'), "success");
        handleClose();
      } else {
        showSnackbar(t('transportation.fleet.vehicleUpdateFailed'), "error");
      }
    } else {
      if (selectedOrganization) {
        const result = await createTruck({
          ...formData,
          organization_id: selectedOrganization.id,
        });
        if (result) {
          showSnackbar(t('transportation.fleet.vehicleAdded'), "success");
          handleClose();
        } else {
          showSnackbar(t('transportation.fleet.vehicleAddFailed'), "error");
        }
      }
    }
  };

  const handleDelete = async (vehicle: TruckSchema) => {
    if (await confirm({ title: t('common.delete'), description: t('transportation.fleet.confirmDelete'), tone: 'danger' })) {
      const result = await deleteTruck(vehicle.id);
      if (result) {
        showSnackbar(t('transportation.fleet.vehicleDeleted'), "success");
      } else {
        showSnackbar(t('transportation.fleet.vehicleDeleteFailed'), "error");
      }
    }
  };

  const handleViewDetails = (vehicle: TruckSchema) => {
    navigate(`/transportation/fleet/${vehicle.id}`);
  };

  const filteredTrucks = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return trucks;
    return trucks.filter((vehicle) => {
      const haystack = [
        vehicle.name,
        vehicle.data?.en_plate_number,
        vehicle.data?.ar_plate_number,
        vehicle.data?.vin,
        vehicle.data?.car_model,
        vehicle.data?.vehicle_type,
        t(`transportation.fleet.status.${vehicle.status}`, { defaultValue: vehicle.status }),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [trucks, search, t]);

  const colCount = 6;

  // Self-setup: open create dialog once when entering form/submit tip phases.
  useEffect(() => {
    if (!tourDialogOpen) return;
    if (open) return;
    handleOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when entering dialog tip phases
  }, [tourDialogOpen]);

  const showAddHint =
    !dismissAddHint &&
    !trucksLoading &&
    !open &&
    (tourAddHint ||
      (helpAddPhase === 'addButton' &&
        !(
          isStep('vehicles') &&
          (tourPhase === 'nav' ||
            tourPhase === 'filters' ||
            tourPhase === 'table' ||
            tourPhase === 'form' ||
            tourPhase === 'submit')
        )));

  // Only show full loading screen if we have NO data yet
  if (trucksLoading && trucks.length === 0) {
    return <LoadingSkeleton variant="table" filters={false} columns={6} rows={6} />;
  }

  return (
    <Box>
      <PageHeader
        icon={<LocalShippingIcon />}
        title={t('transportation.fleet.title')}
        actions={
          canCreateTruck ? (
          <SetupCoachmark
            open={showAddHint}
            title={t('transportation.fleet.addVehicleHintTitle')}
            subtitle={t('transportation.fleet.addVehicleHint')}
            nextLabel={t('selfSetup.tourNext')}
            onNext={() => {
              setDismissAddHint(true);
              if (tourAddHint) {
                advanceTourPhase();
                return;
              }
              if (helpAddPhase === 'addButton') {
                setHelpAddPhase('form');
                handleOpen();
              }
            }}
            onDismiss={() => {
              setDismissAddHint(true);
              if (tourAddHint) dismissTour();
              else if (helpAddPhase === 'addButton') setHelpAddPhase(null);
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setDismissAddHint(true);
                handleOpen();
                if (tourAddHint) setTourPhase('form');
                else if (helpAddPhase === 'addButton') setHelpAddPhase('form');
              }}
              sx={{ textTransform: "none" }}
            >
              {t('transportation.fleet.addVehicle')}
            </Button>
          </SetupCoachmark>
          ) : undefined
        }
      />

      <SetupCoachmark
        open={tourFiltersHint}
        fullWidth
        title={t('selfSetup.tour.vehiclesFiltersTitle')}
        subtitle={t('selfSetup.tour.vehiclesFiltersSubtitle')}
        nextLabel={t('selfSetup.tourNext')}
        onNext={() => advanceTourPhase()}
        onDismiss={() => dismissTour()}
      >
        <Card sx={{ mb: 2 }} elevation={0} variant="outlined">
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <TextField
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('transportation.fleet.searchPlaceholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: 360,
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
              }}
            />
          </CardContent>
        </Card>
      </SetupCoachmark>

      <SetupCoachmark
        open={tourTableHint}
        fullWidth
        title={t('selfSetup.tour.vehiclesTableTitle')}
        subtitle={t('selfSetup.tour.vehiclesTableSubtitle')}
        nextLabel={t('selfSetup.tourNext')}
        onNext={() => advanceTourPhase()}
        onDismiss={() => dismissTour()}
      >
        <Paper elevation={2} sx={elevatedPanelSx}>
        {trucksLoading && <LinearProgress />}
        <TableContainer sx={{ maxHeight: "calc(100vh - 300px)" }}>
          <Table
            stickyHeader
            sx={{
              "& .MuiTableCell-head": { py: 1.5, px: 2 },
              "& .MuiTableCell-body": { py: 1.75, px: 2 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('transportation.fleet.plate')}</TableCell>
                <TableCell align="right">{t('common.capacity')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('transportation.fleet.vin')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTrucks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} sx={{ py: 8, border: 0 }}>
                    <SetupCoachmark
                      open={Boolean(helpRowActionsPhase) && !trucksLoading}
                      fullWidth
                      title={t('transportation.fleet.editDeleteVehicleEmptyTitle')}
                      subtitle={t('transportation.fleet.editDeleteVehicleEmptyHint')}
                      nextLabel={t('selfSetup.tourDone')}
                      onNext={() => setHelpRowActionsPhase(null)}
                      onDismiss={() => setHelpRowActionsPhase(null)}
                    >
                      <Stack alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: alpha(theme.palette.text.secondary, 0.08),
                            color: theme.palette.text.secondary,
                          }}
                        >
                          <InboxIcon fontSize="large" />
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {t('transportation.fleet.noVehicles')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {search
                            ? t('transportation.fleet.noSearchResults')
                            : t('transportation.fleet.noVehiclesHint')}
                        </Typography>
                      </Stack>
                    </SetupCoachmark>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTrucks.map((vehicle, rowIndex) => {
                  const vehicleType = vehicle.data?.vehicle_type?.trim();
                  const vin = vehicle.data?.vin?.trim();
                  const isHelpTargetRow = rowIndex === 0 && Boolean(helpRowActionsPhase);

                  return (
                    <TableRow
                      key={vehicle.id}
                      hover
                      sx={{ transition: "background-color 0.2s ease" }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {vehicle.name}
                        </Typography>
                        {vehicleType ? (
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {t(`transportation.fleet.vehicleTypes.${vehicleType}`, {
                              defaultValue: vehicleType,
                            })}
                            {vehicle.data?.car_model
                              ? ` Â· ${vehicle.data.car_model}`
                              : ""}
                          </Typography>
                        ) : vehicle.data?.car_model ? (
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {vehicle.data.car_model}
                          </Typography>
                        ) : null}
                      </TableCell>

                      <TableCell>
                        <FleetPlateBadge
                          enPlate={vehicle.data?.en_plate_number}
                          arPlate={vehicle.data?.ar_plate_number}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatCapacity(vehicle)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <StatusChip
                          value={vehicle.status}
                          label={t(`transportation.fleet.status.${vehicle.status}`, {
                            defaultValue: vehicle.status,
                          })}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            color: vin ? "text.secondary" : "text.disabled",
                          }}
                          noWrap
                          title={vin || undefined}
                        >
                          {vin || "â€”"}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip title={t('common.viewDetails')}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(vehicle)}
                            sx={{ color: "text.secondary" }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canEditTruck && (isHelpTargetRow && helpRowActionsPhase === 'edit' ? (
                          <SetupCoachmark
                            open
                            title={t('transportation.fleet.editVehicleHintTitle')}
                            subtitle={t('transportation.fleet.editVehicleHint')}
                            nextLabel={t('selfSetup.tourNext')}
                            onNext={() => setHelpRowActionsPhase('delete')}
                            onDismiss={() => setHelpRowActionsPhase(null)}
                          >
                            <Tooltip title={t('common.edit')}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpen(vehicle)}
                                sx={{ color: theme.palette.primary.main }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </SetupCoachmark>
                        ) : (
                          <Tooltip title={t('common.edit')}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpen(vehicle)}
                              sx={{ color: theme.palette.primary.main }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ))}
                        {canDeleteTruck && (isHelpTargetRow && helpRowActionsPhase === 'delete' ? (
                          <SetupCoachmark
                            open
                            title={t('transportation.fleet.deleteVehicleHintTitle')}
                            subtitle={t('transportation.fleet.deleteVehicleHint')}
                            nextLabel={t('selfSetup.tourDone')}
                            onNext={() => setHelpRowActionsPhase(null)}
                            onDismiss={() => setHelpRowActionsPhase(null)}
                          >
                            <Tooltip title={t('common.delete')}>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(vehicle)}
                                sx={{ color: theme.palette.error.main }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </SetupCoachmark>
                        ) : (
                          <Tooltip title={t('common.delete')}>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(vehicle)}
                              sx={{ color: theme.palette.error.main }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ))}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </SetupCoachmark>

      <VehicleFormDialog
        open={open}
        onClose={handleClose}
        isEdit={Boolean(selectedVehicle)}
        formData={formData}
        setFormData={setFormData}
        formErrors={formErrors}
        loading={trucksLoading}
        canSubmit={selectedVehicle ? canEditTruck : canCreateTruck}
        onSubmit={handleSubmit}
        onPlateError={(msg) => showSnackbar(msg, "warning")}
        tourFormHint={tourFormHint}
        tourSubmitHint={tourSubmitHint}
        onTourFormNext={() => {
          if (helpAddPhase === 'form') {
            setHelpAddPhase('submit');
            return;
          }
          advanceTourPhase();
        }}
        onTourSubmitDone={() => {
          if (helpAddPhase === 'submit') {
            finishHelpAddGuide();
            return;
          }
          completeStep('vehicles');
          setOpen(false);
          setSelectedVehicle(null);
        }}
        onTourDismiss={() => {
          if (helpAddPhase) {
            finishHelpAddGuide();
            return;
          }
          dismissTour();
          setOpen(false);
          setSelectedVehicle(null);
        }}
      />

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            borderRadius: "8px",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {confirmDialog}
    </Box>
  );
};

export default FleetManagement;


